import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { ApplicationState } from "../../../../../store";
import { findMany } from "../../../../../store/cardPrompt/cardPromptSlice";
import TextInput from "../../../../../common/formElements/TextInput";
import SelectDropDown from "../../../../../common/formElements/SelectDropDown";
import { FormikProps, useFormikContext } from "formik";
import { Data } from "../../../../../store/terminal/types";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";

interface TerminalInfoProps {
  testOnly: boolean;
  isUpdateAllowed?: boolean;
}

const EditTerminalInfo = ({ testOnly, isUpdateAllowed }: TerminalInfoProps) => {
  const dispatch = useDispatch();
  const formProps = useFormikContext<FormikProps<Data>>();
  const values = formProps.values as unknown as Data;
  const isDMG = process.env.IDP === "Auth0" ? true : false;
  const isQuestTerminal = () =>
    values &&
    values.attributes &&
    values.attributes.Manufacturer &&
    values.attributes.Manufacturer.toUpperCase() === "QUEST";
  const nabTidIdx =
    values.attributes.Processor &&
    values.attributes.Processor.findIndex(
      (prc) =>
        prc.ProcessorName && prc.ProcessorName.toLowerCase().includes("nab")
    );
  const isNabTidAvailable = typeof nabTidIdx === "number" && nabTidIdx !== -1;

  useEffect(() => {
    // @ts-ignore
    dispatch(findMany());
  }, []);

  const emvOptions =
    useSelector((state: ApplicationState) => state.emv.data) ?? [];

  const closeLoopOptions =
    useSelector((state: ApplicationState) => state.closeloop.data) ?? [];

  const cards = useSelector((state: ApplicationState) =>
    state.card.data ? state.card.data.data : []
  );

  const tokenWalletOptions =
    useSelector((state: ApplicationState) => state.tokenWallet.data) ?? [];

  const availableCardPrompts =
    useSelector((store: ApplicationState) => store.cardPrompt.entries) ?? [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextInput
          fieldname="attributes.TerminalType"
          label="Terminal Type"
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          fieldname="attributes.SerNo"
          label="Serial Number"
          InputProps={{ readOnly: testOnly }}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          fieldname="attributes.TID"
          label="TID"
          disabled={!isUpdateAllowed}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput
          fieldname="attributes.POIID"
          label="POI Device ID"
          disabled={!isUpdateAllowed}
        />
      </Grid>
      {emvOptions && emvOptions.length ? (
        <Grid item xs={12} md={4}>
          <SelectDropDown
            disabled={!isUpdateAllowed}
            fieldname="attributes.EMVConfigName"
            label="EMV"
            options={emvOptions.map((option) => option.attributes.ConfigName)}
          />
        </Grid>
      ) : null}
      {cards && cards.length ? (
        <Grid item xs={12} md={4}>
          <SelectDropDown
            disabled={!isUpdateAllowed}
            fieldname="attributes.CPATName"
            label="CPAT Name"
            options={cards ? cards.map((card) => card.attributes.Name) : []}
          />
        </Grid>
      ) : null}
      {isDMG && !isQuestTerminal() && closeLoopOptions && closeLoopOptions.length ? (
        <Grid item xs={12} md={4}>
          <SelectDropDown
            disabled={!isUpdateAllowed}
            fieldname="attributes.CloseLoopConfigName"
            label="Close Loop Config"
            options={closeLoopOptions?.map(
              (option) => option.attributes.ConfigName
            )}
          />
        </Grid>
      ) : null}

      {tokenWalletOptions && tokenWalletOptions.length ? (
        <Grid item xs={12} md={4}>
          <SelectDropDown
            disabled={!isUpdateAllowed}
            fieldname="attributes.TokenWalletFileConfigName"
            label="Token Wallet Config"
            options={tokenWalletOptions?.map(
              (option) => option.attributes.Name
            )}
          />
        </Grid>
      ) : null}

      {availableCardPrompts && availableCardPrompts.length ? (
        <Grid item xs={12} md={4}>
          <SelectDropDown
            disabled={!isUpdateAllowed}
            fieldname="attributes.CardPrompts"
            label="Card Prompt"
            options={availableCardPrompts.map(
              (prompt) => prompt.attributes.Name
            )}
          />
        </Grid>
      ) : null}
      {isNabTidAvailable ? (
        <Grid item xs={12} md={4}>
          <TextInput
            fieldname={`attributes.Processor[${nabTidIdx}].TID`}
            label="NAB TID"
            disabled
          />
        </Grid>
      ) : null}
      <Grid item xs={12} md={4}>
        <OptionalToolTip tooltip="Low Value Transaction processing only applies to AMEX and Visa cards, and the customer is not asked to provide card verification for transactions below the limit.">
          <TextInput
              fieldname="attributes.LowValueTransactionLimit"
              label="Low Value Transaction Limit"
              disabled={!isUpdateAllowed}
            />
        </OptionalToolTip>
      </Grid>
    </Grid>
  );
};

export default memo(EditTerminalInfo);
